'use client';

import {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import NiceModal from '@ebay/nice-modal-react';
import { ChatTypingDots } from '@components/common';
import { ChatMessageComponent } from '@components/templates/homepage/chat/ChatMessageComponent';
import { ChatJobModal } from '@components/templates/homepage/chat/ChatJobModal';
import ChatTalentModal from '@components/templates/homepage/chat/ChatTalentModal';
import { recommendationToJobItem } from '@components/templates/homepage/chat/jobRecommendationToJobItem';
import type { Message, StreamState } from '@components/templates/bia/hooks/useStreamStateV2';
import { useAppSelector } from '@store/index';
import { getJobBulkApplyFeed } from '@store/reducers/jobSelector';
import { BulkApplyModal } from '@components/templates/job/apply/BulkApplyModal';
import { isBestieGreetingPrompt } from '@hooks/features/bestieGreetingPrompt';

interface BestieMessagesListProps {
  stream: StreamState | null;
  locale: Locale;
  /** True while the proactive opener (chat/invoke, authenticated chat-page entry) is in flight.
   *  `stream` stays null for that whole call — nothing touches it until the reply seeds a
   *  message — so without this the typing indicator never appears and the page is blank.
   *  Optional: callers that don't have a pending opener to report simply omit it. */
  isGreetingPending?: boolean;
}

const messageHasResults = (m?: Message): boolean => (
  (m?.response_metadata?.customData?.jobSearchResults?.items?.length ?? 0) > 0
  || (m?.response_metadata?.customData?.jobRecommendationResults?.items?.length ?? 0) > 0
  || (m?.response_metadata?.customData?.resumeSearchResults?.items?.length ?? 0) > 0
);

export const BestieMessagesList = ({ stream, locale, isGreetingPending }: BestieMessagesListProps) => {
  const lastMessageRef = useRef<HTMLDivElement | null>(null);
  const [talentsNavigationList, setTalentsNavigationList] = useState<ApiSchema<'ResumeCardData'>[]>([]);
  const [appliedJobs, setAppliedJobs] = useState<Record<string, string>>({});

  useEffect(() => {
    const el = lastMessageRef.current;
    if (!el) return;
    // The messages pane now flows in the MC <main> scroller (not its own
    // overflow container), so scroll the nearest scrollable ancestor fully to
    // the bottom. Scrolling past the last message reveals the column's trailing
    // pb-28 padding, keeping the newest message clear of the fixed prompt bar.
    let node: HTMLElement | null = el.parentElement;
    while (node) {
      const { overflowY } = window.getComputedStyle(node);
      if (overflowY === 'auto' || overflowY === 'scroll') {
        node.scrollTop = node.scrollHeight;
        return;
      }
      node = node.parentElement;
    }
  }, [stream?.messages, stream?.status]);

  const markJobApplied = useCallback((jobSlug: string) => {
    if (!jobSlug) return;
    setAppliedJobs((current) => {
      if (current[jobSlug]) return current;
      return { ...current, [jobSlug]: new Date().toISOString() };
    });
  }, []);

  const withAppliedState = useCallback((job: ApiSchema<'JobItem'>): ApiSchema<'JobItem'> => {
    const appliedAt = job.slug ? appliedJobs[job.slug] : undefined;
    if (!appliedAt || job.appliedAt) return job;
    return { ...job, appliedAt };
  }, [appliedJobs]);

  const jobsArray = useMemo(() => stream?.messages
    ?.filter((item) => (
      (item?.response_metadata?.customData?.jobSearchResults?.items?.length ?? 0) > 0
      || (item?.response_metadata?.customData?.jobRecommendationResults?.items?.length ?? 0) > 0
    ))
    .reduce<{ items: ApiSchema<'JobItem'>[] | undefined }[]>((acc, item) => {
      const searchItems = item?.response_metadata?.customData?.jobSearchResults?.items
        ?.map((i) => withAppliedState(i.data)) ?? [];
      const recommendationItems = item?.response_metadata?.customData?.jobRecommendationResults?.items
        ?.filter((recommendation) => recommendation.type !== 'lead')
        .map((recommendation) => withAppliedState(recommendationToJobItem(recommendation))) ?? [];
      acc.push({ items: [...searchItems, ...recommendationItems] });
      return acc;
    }, []), [stream?.messages, withAppliedState]);

  const onCvDetailOpen = useCallback((message: Message) => {
    setTalentsNavigationList(message.response_metadata?.customData?.resumeSearchResults?.items || []);
  }, []);

  const messagesList = useMemo(() => {
    if (!stream?.messages) return [];
    // The greeting priming turn is a real message in the thread but must never be shown.
    const visible = stream.messages.filter((m) => !isBestieGreetingPrompt(m));
    // Use a Map to deduplicate by message id.
    const ordered = [...new Map(visible.map((m) => [m.id, m])).values()];
    // Tool results arrive before the AI text in streaming; swap consecutive [results, text-only AI]
    // pairs so text appears above results in the UI.
    for (let i = 0; i < ordered.length - 1; i += 1) {
      const curr = ordered[i];
      const next = ordered[i + 1];
      if (messageHasResults(curr) && !messageHasResults(next) && next?.type === 'ai') {
        ordered[i] = next;
        ordered[i + 1] = curr;
      }
    }
    return ordered.map((m, index) => (
      <div key={`message-container-${m.id}`} className="flex flex-col gap-4">
        <ChatMessageComponent
          key={`message-${m.id}`}
          message={{ ...m }}
          ref={index === ordered.length - 1 ? lastMessageRef : null}
          locale={locale}
          onInteraction={onCvDetailOpen}
          forceJobModalOnMc
          jobModalUsesCurrentUrl
          jobApplyWithAuthModal
          jobApplyStaysOnPage
          appliedJobs={appliedJobs}
        />
      </div>
    ));
  }, [stream?.messages, locale, onCvDetailOpen, appliedJobs]);

  const bulkApplyFeed = useAppSelector(getJobBulkApplyFeed);
  useEffect(() => {
    if (bulkApplyFeed?.length) {
      void NiceModal.show(BulkApplyModal, { jobList: bulkApplyFeed, locale });
    }
  }, [bulkApplyFeed, locale]);

  return (
    <>
      <div
        className="container mx-auto flex flex-col gap-5 px-4 py-6"
      >
        {messagesList}
        {(stream?.status === 'inflight' || isGreetingPending) && <ChatTypingDots />}
      </div>

      <ChatJobModal locale={locale} jobsArray={jobsArray} onJobApplied={markJobApplied} />
      <ChatTalentModal talentsArray={[{ items: talentsNavigationList }]} />
    </>
  );
};
